import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { tokens } from "../theme";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

/**
 * Modern, reusable Alert Dialog Component
 * 
 * @param {string} title - Dialog title
 * @param {string} message - Main message to display
 * @param {string} description - Optional additional description
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Callback when dialog closes
 * @param {function} onConfirm - Optional callback for confirm action
 * @param {string} type - Alert type: 'warning', 'error', 'info', 'success'
 * @param {string} confirmText - Text for confirm button (default: 'OK')
 * @param {string} cancelText - Text for cancel button (default: 'Cancel')
 * @param {boolean} showCancel - Show cancel button (default: true if onConfirm exists)
 */
export default function AlertDialog({
  title = "Alert",
  message,
  description,
  open,
  onClose,
  onConfirm,
  type = "warning",
  confirmText,
  cancelText = "Cancel",
  showCancel,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Determine if cancel button should be shown
  const shouldShowCancel = showCancel !== undefined ? showCancel : !!onConfirm;

  // Icon and color configuration based on type
  const typeConfig = {
    warning: {
      icon: <WarningAmberIcon sx={{ fontSize: 32 }} />,
      iconColor: "#ffa726", // Orange color
      iconBg: `rgba(255, 167, 38, 0.2)`,
      gradientStart: "#fb8c00",
      gradientEnd: "#f57c00",
      buttonColor: "#fb8c00",
      buttonHover: "#f57c00",
    },
    error: {
      icon: <ErrorOutlineIcon sx={{ fontSize: 32 }} />,
      iconColor: colors.redAccent[400],
      iconBg: `${colors.redAccent[800]}40`,
      gradientStart: colors.redAccent[600],
      gradientEnd: colors.redAccent[700],
      buttonColor: colors.redAccent[500],
      buttonHover: colors.redAccent[600],
    },
    info: {
      icon: <InfoOutlinedIcon sx={{ fontSize: 32 }} />,
      iconColor: colors.blueAccent[400],
      iconBg: `${colors.blueAccent[800]}40`,
      gradientStart: colors.blueAccent[600],
      gradientEnd: colors.blueAccent[700],
      buttonColor: colors.blueAccent[500],
      buttonHover: colors.blueAccent[600],
    },
    success: {
      icon: <CheckCircleOutlineIcon sx={{ fontSize: 32 }} />,
      iconColor: colors.greenAccent[400],
      iconBg: `${colors.greenAccent[800]}40`,
      gradientStart: colors.greenAccent[600],
      gradientEnd: colors.greenAccent[700],
      buttonColor: colors.greenAccent[500],
      buttonHover: colors.greenAccent[600],
    },
  };

  const config = typeConfig[type] || typeConfig.warning;

  // Default confirm text based on type
  const defaultConfirmText = onConfirm ? "Confirm" : "OK";
  const finalConfirmText = confirmText || defaultConfirmText;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[400],
          borderRadius: 4,
          overflow: "hidden",
          border: `2px solid ${config.iconColor}40`,
          boxShadow: `0 20px 60px ${colors.primary[900]}80`,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 4,
          py: 3,
          background: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          borderBottom: `2px solid ${config.iconColor}30`,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            backgroundColor: config.iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 8px 24px ${colors.primary[900]}50`,
            border: `2px solid ${config.iconColor}30`,
          }}
        >
          {React.cloneElement(config.icon, { 
            sx: { fontSize: 32, color: config.iconColor } 
          })}
        </Box>
        <Typography
          variant="h4"
          sx={{ 
            color: colors.grey[100], 
            fontWeight: 700, 
            letterSpacing: 0.3,
            flex: 1,
          }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 4,
          py: 4,
          backgroundColor: colors.primary[400],
        }}
      >
        <Stack spacing={2.5}>
          <Typography
            variant="h6"
            sx={{
              color: colors.grey[100],
              fontWeight: 600,
              letterSpacing: 0.2,
              lineHeight: 1.6,
            }}
          >
            {message}
          </Typography>
          {description && (
            <Box
              sx={{
                p: 2.5,
                borderRadius: 2,
                backgroundColor: `${colors.primary[600]}80`,
                border: `1px solid ${colors.primary[200]}30`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: colors.grey[300],
                  lineHeight: 1.7,
                  whiteSpace: "pre-line",
                }}
              >
                {description}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 3,
          backgroundColor: `${colors.primary[500]}80`,
          borderTop: `1px solid ${colors.primary[200]}20`,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        {shouldShowCancel && (
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              minWidth: 120,
              borderRadius: 2.5,
              borderColor: `${colors.grey[600]}80`,
              color: colors.grey[200],
              fontWeight: 600,
              textTransform: "none",
              letterSpacing: 0.3,
              px: 3,
              py: 1.2,
              "&:hover": {
                borderColor: colors.grey[500],
                backgroundColor: `${colors.grey[700]}30`,
              },
            }}
          >
            {cancelText}
          </Button>
        )}
        <Button
          onClick={() => {
            if (onConfirm) {
              onConfirm();
            } else {
              onClose();
            }
          }}
          variant="contained"
          sx={{
            minWidth: 120,
            borderRadius: 2.5,
            textTransform: "none",
            letterSpacing: 0.3,
            fontWeight: 700,
            color: "white",
            px: 4,
            py: 1.2,
            background: `linear-gradient(135deg, ${config.gradientStart} 0%, ${config.gradientEnd} 100%)`,
            boxShadow: `0 8px 24px ${config.buttonColor}40`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 12px 32px ${config.buttonColor}60`,
            },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {finalConfirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

